import { useState } from 'react';
import type { CommentAttachmentDto } from '@inference.engine/workspace-api-client/web';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { AlertCircle, Link2, X } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const FILENAME_TRUNCATE_AT = 20;
const COLLAPSED_LIMIT = 3;

interface Props {
  attachments: CommentAttachmentDto[];
  /**
   * When provided, each chip renders a remove (X) button that calls back with
   * the attachment id. Omit to render the list read-only.
   */
  onRemove?: (attachmentId: string) => void;
}

export function AttachmentChipList({ attachments, onRemove }: Props) {
  const [expanded, setExpanded] = useState(false);

  const safeAttachments = attachments.filter(
    (a): a is CommentAttachmentDto => Boolean(a) && typeof a.id === 'string',
  );
  if (safeAttachments.length === 0) return null;

  const shouldCollapse = !expanded && safeAttachments.length > COLLAPSED_LIMIT;
  const visible = shouldCollapse ? safeAttachments.slice(0, COLLAPSED_LIMIT) : safeAttachments;
  const overflowCount = shouldCollapse ? safeAttachments.length - COLLAPSED_LIMIT : 0;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-row flex-wrap gap-2">
        {visible.map((a) => (
          <AttachmentChip key={a.id} attachment={a} onRemove={onRemove} />
        ))}
        {shouldCollapse && (
          <OverflowChip count={overflowCount} onExpand={() => setExpanded(true)} />
        )}
      </div>
    </TooltipProvider>
  );
}

function truncateFilename(filename: string): string {
  if (filename.length <= FILENAME_TRUNCATE_AT) return filename;
  return `${filename.slice(0, FILENAME_TRUNCATE_AT)}…`;
}

const chipBaseClasses =
  'inline-flex h-7 items-center gap-1.5 rounded-full bg-muted text-sm text-foreground transition-colors';

function isBrowserPreviewable(mimeType: string): boolean {
  return (
    mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType === 'application/pdf'
  );
}

function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: CommentAttachmentDto;
  onRemove?: (attachmentId: string) => void;
}) {
  const { t } = useTranslation('projects');

  const broken = attachment.downloadUrl == null;
  const isLink = !broken;
  const previewable = isBrowserPreviewable(attachment.mimeType);
  const truncated = truncateFilename(attachment.filename);
  const isTruncated = truncated !== attachment.filename;

  const body = (
    <>
      {broken ? (
        <AlertCircle className="size-3.5 shrink-0 text-destructive" />
      ) : (
        <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className="whitespace-nowrap">{truncated}</span>
      {onRemove && (
        <button
          type="button"
          aria-label={t('projectComment.attachmentChipList.removeAttachment', {
            filename: attachment.filename,
          })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(attachment.id);
          }}
          className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      )}
    </>
  );

  const chipClasses = clsx(
    chipBaseClasses,
    'pl-3',
    onRemove ? 'pr-1.5' : 'pr-3',
    broken && 'bg-destructive/10 text-destructive',
    isLink && 'cursor-pointer hover:bg-accent',
  );

  const chip = isLink ? (
    <a
      href={attachment.downloadUrl ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      download={previewable ? undefined : attachment.filename}
      className={clsx(chipClasses, 'no-underline')}
    >
      {body}
    </a>
  ) : (
    <span className={chipClasses}>{body}</span>
  );

  if (!isTruncated) return chip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>{attachment.filename}</TooltipContent>
    </Tooltip>
  );
}

function OverflowChip({ count, onExpand }: { count: number; onExpand: () => void }) {
  const { t } = useTranslation('projects');

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label={t('projectComment.attachmentChipList.showMoreAttachments', { count })}
      className={clsx(chipBaseClasses, 'cursor-pointer px-3 font-medium hover:bg-accent')}
    >
      +{count}
    </button>
  );
}
